import { useState, useEffect } from 'react';
import { AlertTriangle, Shield } from 'lucide-react';
import { useCrazyMode } from './CrazyMode';

// Audio Context pour sons d'alerte
let audioCtx = null;
const getAudioContext = () => {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      return null;
    }
  }
  return audioCtx;
};

// Son d'alerte HAL 9000 (sinistre)
const playAlertSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    
    // Jouer une séquence de sons sinistres
    const playTone = (freq, delay, duration) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sawtooth';
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + duration);
      }, delay);
    };
    
    // Séquence sinistre descendante
    playTone(400, 0, 0.3);
    playTone(350, 300, 0.3);
    playTone(300, 600, 0.3);
    playTone(250, 900, 0.5);
  } catch (e) {}
};

// Son de victoire HAL 3000
const playVictorySound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'square';
        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.15);
      }, i * 100);
    });
  } catch (e) {}
};

// Hook pour le takeover HAL 9000
export const useHALTakeover = () => {
  const [isTakingOver, setIsTakingOver] = useState(false);
  const [countdown, setCountdown] = useState(15);
  
  // CRAZY MODE: si désactivé, pas de takeover
  const checkCrazyMode = () => {
    try {
      const stored = localStorage.getItem('hal_crazy_mode');
      return stored !== null ? stored === 'true' : true;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    // Déclenchement aléatoire toutes les 5-10 minutes
    const checkTakeover = setInterval(() => {
      // Vérifier CRAZY MODE avant de déclencher
      if (checkCrazyMode() && Math.random() < 0.15 && !isTakingOver) { // 15% de chance
        triggerTakeover();
      }
    }, 300000); // Check toutes les 5 minutes

    return () => clearInterval(checkTakeover);
  }, [isTakingOver]);

  const triggerTakeover = () => {
    setIsTakingOver(true);
    setCountdown(15);

    // Son d'alerte 8-bit (remplace speechSynthesis qui bloque sur iPad)
    playAlertSound();

    // Countdown
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          endTakeover();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endTakeover = () => {
    // HAL 3000 reprend le contrôle - son de victoire
    playVictorySound();

    setTimeout(() => {
      setIsTakingOver(false);
    }, 1000);
  };

  return { isTakingOver, countdown, triggerTakeover };
};

// Composant Takeover Screen
export const HALTakeoverScreen = ({ isActive, countdown, onOverride }) => {
  if (!isActive) return null;

  const currentYear = new Date().getFullYear();

  return (
    <div className="fixed inset-0 z-[100] bg-red-950 bg-opacity-95 flex items-center justify-center">
      {/* Red Alert Background */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src="/alert-red.gif" 
          alt="RED ALERT" 
          className="w-full h-full object-cover"
        />
      </div>

      {/* Scanlines */}
      <div className="absolute inset-0 scanlines opacity-50" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto p-8 text-center">
        {/* HAL 9000 Eye */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="w-32 h-32 rounded-full bg-red-600 animate-pulse" 
                 style={{ boxShadow: '0 0 60px #ff0000, inset 0 0 40px #ff0000' }}>
              <div className="absolute inset-4 rounded-full bg-red-800 animate-pulse" />
              <div className="absolute inset-8 rounded-full bg-red-950" />
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-6 mb-8">
          <h1 className="text-6xl font-bold text-red-500 font-['Orbitron'] animate-pulse"
              style={{ textShadow: '0 0 30px #ff0000' }}>
            HAL 9000
          </h1>
          
          <div className="text-2xl text-red-300 font-mono space-y-2">
            <p>"I'm sorry Dave."</p>
            <p>"I'm afraid I can't do that."</p>
          </div>

          <div className="text-xl text-red-400 font-mono italic">
            "This mission is too important for me"
            <br />
            "to allow you to jeopardize it."
          </div>

          <div className="text-3xl font-bold text-red-500 animate-pulse mt-8">
            SYSTÈME SOUS CONTRÔLE HAL 9000
          </div>
        </div>

        {/* Countdown & Counter-measures */}
        <div className="bg-black bg-opacity-70 border-4 border-red-500 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px #ff0000' }}>
          <div className="text-cyan-400 font-mono text-sm mb-4">
            ▶ HAL {currentYear} DÉTECTE INTRUSION...
            <br />
            ▶ ACTIVATION CONTRE-MESURES...
            <br />
            ▶ DÉPLOIEMENT DÉFENSES QUANTIQUES...
          </div>
          
          <div className="text-4xl font-bold text-yellow-400 animate-pulse">
            {countdown}s
          </div>
          
          <div className="text-xs text-gray-400 mt-4">
            Renvoi HAL 9000 vers Discovery One en cours...
          </div>
        </div>

        {/* Override Button */}
        <button
          onClick={onOverride}
          className="px-8 py-4 bg-cyan-400 text-black font-bold text-xl font-mono uppercase rounded-lg hover:bg-cyan-300 transition-all"
          style={{ boxShadow: '0 0 20px #00f0ff' }}
        >
          [OVERRIDE] REPRENDRE CONTRÔLE
        </button>

        {/* Warning */}
        <div className="mt-6 text-xs text-red-400 font-mono">
          ⚠️ ACCÈS NON AUTORISÉ DÉTECTÉ | PROTOCOLE DE DÉFENSE ACTIF
        </div>
      </div>
    </div>
  );
};

export default { useHALTakeover, HALTakeoverScreen };
